import type { Editor } from '@tiptap/core';
import { Button } from 'antd';
import { useState, useEffect } from 'react';
import { UndoOutlined, RedoOutlined, BoldOutlined, ItalicOutlined, TableOutlined } from '@ant-design/icons';
import { HeadingDropdownMenu } from './HeadingDropdownMenu';
import { ListDropdownMenu } from './ListDropdownMenu';
import { TextAlignDropdownMenu } from './TextAlignDropdownMenu';
import { LinkPopover } from './LinkPopover';
import { UndoRedoToolbar } from './UndoRedoToolbar';
import { ResetToolbar } from './ResetToolbar';

interface ToolbarProps {
  editor: Editor;
}

export const Toolbar = ({ editor }: ToolbarProps) => {
  const [isBoldActive, setIsBoldActive] = useState(false);
  const [isItalicActive, setIsItalicActive] = useState(false);
  const isEditable = editor.isEditable;

  useEffect(() => {
    const updateActiveStates = () => {
      setIsBoldActive(editor.isActive('bold'));
      setIsItalicActive(editor.isActive('italic'));
    };

    updateActiveStates();
    editor.on('selectionUpdate', updateActiveStates);
    editor.on('focus', updateActiveStates);
    editor.on('blur', updateActiveStates);

    return () => {
      editor.off('selectionUpdate', updateActiveStates);
      editor.off('focus', updateActiveStates);
      editor.off('blur', updateActiveStates);
    };
  }, [editor]);

  const toggleBold = () => {
    if (isEditable) editor.chain().focus().toggleBold().run();
  };

  const toggleItalic = () => {
    if (isEditable) editor.chain().focus().toggleItalic().run();
  };

  const insertTable = () => {
    if (isEditable) editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const undo = () => {
    if (isEditable) editor.chain().focus().undo().run();
  };

  const redo = () => {
    if (isEditable) editor.chain().focus().redo().run();
  };

  return (
    <div className="toolbar">
      <Button onClick={undo} disabled={!isEditable || !editor.can().undo()} title="Undo" icon={<UndoOutlined />} />
      <Button onClick={redo} disabled={!isEditable || !editor.can().redo()} title="Redo" icon={<RedoOutlined />} />
      <div className="separator"></div>
      <HeadingDropdownMenu editor={editor} levels={[1, 2, 3, 4, 5, 6]} />
      <div className="separator"></div>
      <Button
        type={isBoldActive ? 'primary' : 'default'}
        onClick={toggleBold}
        disabled={!isEditable}
        title="Bold"
        icon={<BoldOutlined />}
      />
      <Button
        type={isItalicActive ? 'primary' : 'default'}
        onClick={toggleItalic}
        disabled={!isEditable}
        title="Italic"
        icon={<ItalicOutlined />}
      />
      <div className="separator"></div>
      <ListDropdownMenu editor={editor} />
      <TextAlignDropdownMenu editor={editor} />
      <div className="separator"></div>
      <input
        type="color"
        defaultValue="#000000"
        onChange={(e) => isEditable && editor.chain().focus().setColor(e.target.value).run()}
        title="Text Color"
        className="color-picker"
        style={{
          width: '32px',
          height: '32px',
          cursor: isEditable ? 'pointer' : 'not-allowed',
          border: 'none',
          padding: 0,
          opacity: isEditable ? 1 : 0.5
        }}
        disabled={!isEditable}
      />
      <div className="separator"></div>
      <LinkPopover editor={editor} />
      <Button
        onClick={insertTable}
        type={editor.isActive('table') ? 'primary' : 'default'}
        disabled={!isEditable}
        title="Insert Table"
        icon={<TableOutlined />}
      />
    </div>
  );
};
